// Edit planner for CREATE / ALTER / DROP TRIGGER (v2 Slice D6).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D6.
//
// All three ops append a fresh statement after the source statement
// (DSE2-7: no coalescing by default). The source statement kind is
// unrestricted — each op names its target via its own fields.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterTrigger,
  synthesizeCreateTrigger,
  synthesizeDropTrigger,
} from './dialect.trigger';

type TriggerEdit = Extract<
  DdlEdit,
  { op: 'create-trigger' | 'alter-trigger' | 'drop-trigger' }
>;

export function planTriggerEdit(
  ctx: DdlEditCtx,
  edit: TriggerEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForTrigger(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForTrigger(ctx: DdlEditCtx, edit: TriggerEdit): string {
  switch (edit.op) {
    case 'create-trigger':
      return synthesizeCreateTrigger(ctx.engine, edit.trigger);
    case 'alter-trigger':
      return synthesizeAlterTrigger(
        ctx.engine,
        edit.triggerName,
        edit.target,
        edit.change,
      );
    case 'drop-trigger':
      return synthesizeDropTrigger(
        ctx.engine,
        edit.triggerName,
        edit.target,
        edit.ifExists ?? false,
        edit.cascade ?? false,
      );
  }
}
